import { TILE } from '../sim/constants';
import { worldToScreenX, worldToScreenY } from './projection';
import type { Camera, Viewport } from './projection';

/*
 * ערפל קרב — שכבת התצוגה.
 *
 * **הקנבס הוא ברזולוציית אריח: פיקסל אחד לאריח.** מפה של 96×96 היא תמונה של
 * 96×96 שנמתחת על המסך, ומכאן שני דברים בחינם: הציור עצמו הוא `putImageData`
 * אחד ו-`drawImage` אחד לפריים בלי קשר לזום, והמתיחה עם החלקה נותנת **קצה רך**
 * במקום ריבועים — שהם ההבדל בין "ערפל" ל"שחמט".
 *
 * **דרגה אחת בלבד: שטח שנחשף נשאר חשוף.**
 *
 * הייתה כאן דרגה שנייה — "ערפל" חוזר על שטח שראית ואינך רואה עכשיו — וזו הצורה
 * של RA2. בר ביקש להוריד אותה במפורש: ערפל שמתפוגג צריך להיעלם ולא לחזור.
 * ההיגיון מאחורי הבקשה מחזיק: בטלפון המסך מראה פינה קטנה מהמפה, וכיסוי חוזר של
 * כל מה שיצאת ממנו הופך את הניווט לעונש.
 *
 * **וניסינו להחזיר אותה כהצללה עדינה — וזה נכשל, בדרך מלמדת.** הבעיה שהיא נועדה
 * לפתור (יחידות אויב שנעלמות בשטח שנראה גלוי) אמיתית, אבל ההצללה נגזרת ממסכת
 * "נראה עכשיו" — מסכה שמשתנה **בכל טיק**, כי כל יחידה שזזה גוררת איתה עיגול של
 * אור. התוצאה הייתה מפה שנושמת בלי הרף, ובר דיווח שזה מעצבן הרבה יותר מהבעיה
 * המקורית. **בעיה סטטית הפכה לבעיה דינמית, וזו החמרה.**
 *
 * הפתרון עבר למקום שבו הבעיה באמת נמצאת: **היחידה, לא המפה.** יחידת אויב שיצאה
 * מהטווח נשארת מצוירת במקום שבו נראתה לאחרונה ודוהה משם (`render/memory.ts`).
 * שום דבר לא נעלם פתאום, ושום דבר על המפה לא זז.
 */

/** שקיפות הצל. שטח שנחשף הוא 0 — שקוף לגמרי, לתמיד. */
const SHROUD_ALPHA = 255;

/**
 * הדרגה של אריח יחיד.
 *
 * מחולצת מהלולאה כדי שהיא תהיה **בדיקה בלי קנבס**: `createFogLayer` דורש
 * `getContext('2d')`, שאינו אמין בסביבת הטסטים.
 */
export function fogAlpha(exploredByte: number): number {
    return exploredByte === 1 ? 0 : SHROUD_ALPHA;
}

export interface FogLayer {
    readonly canvas: HTMLCanvasElement;
    readonly image: ImageData;
    /** ההפניה ל-`explored` שממנה נבנתה התמונה, לזיהוי "אין מה לצייר מחדש". */
    exploredRef: Uint8Array | null;
}

export function createFogLayer(w: number, h: number): FogLayer {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('אין הקשר 2d לשכבת הערפל');
    return {
        canvas,
        image: ctx.createImageData(w, h),
        exploredRef: null,
    };
}

/**
 * מרענן את תמונת הערפל. מדלג כשלא התגלה שטח חדש.
 *
 * ‏`explored` מתחלף בהפניה **רק** בטיק שבו התגלה שטח חדש (ראו `sim/vision.ts`),
 * ולכן ההשוואה הזו לבדה חוסכת את הבנייה מחדש ברוב הפריימים — ומאז שהערפל אינו
 * חוזר, מסכת "נראה עכשיו" אינה משפיעה על התמונה בכלל. **וזו בדיוק הנקודה:**
 * תמונה שתלויה במסכה הזו מתעדכנת בכל טיק, כלומר המפה זזה כל הזמן.
 */
export function updateFog(layer: FogLayer, explored: Uint8Array): void {
    if (layer.exploredRef === explored) return;
    layer.exploredRef = explored;

    const data = layer.image.data;
    for (let i = 0; i < explored.length; i++) {
        const alpha = fogAlpha(explored[i] ?? 0);
        const p = i * 4;
        data[p] = 0;
        data[p + 1] = 0;
        data[p + 2] = 0;
        data[p + 3] = alpha;
    }
    const ctx = layer.canvas.getContext('2d');
    ctx?.putImageData(layer.image, 0, 0);
}

/** מותח את שכבת הערפל על שדה הקרב הנראה. */
export function drawFog(
    ctx: CanvasRenderingContext2D,
    layer: FogLayer,
    cam: Camera,
    vp: Viewport,
    map: { readonly w: number; readonly h: number },
): void {
    const x = worldToScreenX(0, cam, vp);
    const y = worldToScreenY(0, cam, vp);
    const w = map.w * TILE * (cam.zoom / TILE);
    const h = map.h * TILE * (cam.zoom / TILE);

    const smoothing = ctx.imageSmoothingEnabled;
    // ההחלקה **היא** הפיצ'ר כאן, בניגוד לטרשת שבה היא הייתה מטשטשת מרקם.
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(layer.canvas, x, y, w, h);
    ctx.imageSmoothingEnabled = smoothing;
}
